import type { Difficulty } from "./match.ts";

/**
 * Morte e reviver (doc 02 §15–§18; doc 07 J8–J9). Ao morrer, o jogador deixa a Cruz de Beira de
 * Estrada e uma Trouxa com todo o inventário; volta na Cruz (um aliado segura a interação por 3 s,
 * a até 3 m, só de dia, depois da espera) ou na Carranca (pago, a qualquer hora). Na Treta, sem
 * reviver. Valores são hipóteses de balanceamento.
 */
export const DEATH = {
  /** Segurar a interação na Cruz (s) e alcance (m). */
  reviveHold: 3,
  reviveReach: 3,
  /** Espera por morte: dobra a cada vez, com teto. */
  waits: [20, 40, 80, 160, 240],
  /** Ao voltar: Vida, Fome mínima e invulnerabilidade real (s). O Patuá volta vazio. */
  hpFraction: 0.6,
  minHunger: 40,
  invulnerable: 3,
  /** Carranca: alcance para pagar (m). */
  carrancaReach: 4,
  /** Sozinho e morto (F54): prazo para pagar a própria volta ou desistir (s); sem escolha, derrota. */
  soloDecision: 120,
  /** Câmera de morte (s), altura que sobe (m) e distância do espectador (m). */
  deathCamera: 3,
  deathCameraRise: 2.5,
  spectatorDistance: 5,
  /** Câmera livre do espectador (só no Mutirão): até 40 m do aliado seguido. */
  freeCameraRadius: 40,
} as const;

/** Espera da `deaths`-ésima morte (1 = primeira): 20, 40, 80, 160 e 240 s (teto). */
export function reviveWait(deaths: number): number {
  const i = Math.max(1, deaths) - 1;
  return DEATH.waits[Math.min(i, DEATH.waits.length - 1)] as number;
}

const CARRANCA_MUL: Record<Difficulty, number> = { tranquilo: 0.7, arretado: 1, sinistro: 1.4 };

/**
 * Preço de reviver na Carranca (doc 02 §17): round(30 × m × (1 + (dia − 1)/4)), teto de 300,
 * com o meio arredondado para o par (bate com os exemplos do doc). O Dia 0 conta como o 1º.
 */
export function carrancaPrice(day: number, difficulty: Difficulty): number {
  return Math.min(300, roundHalfEven(30 * CARRANCA_MUL[difficulty] * (1 + (Math.max(1, day) - 1) / 4)));
}

/** Arredonda o meio para o par (os exemplos do doc 02: 112,5 → 112; 157,5 → 158). */
function roundHalfEven(x: number): number {
  const floor = Math.floor(x);
  const frac = x - floor;
  if (Math.abs(frac - 0.5) > 1e-9) return Math.round(x);
  return floor % 2 === 0 ? floor : floor + 1;
}

/** Estruturas da morte (nomes na mira). */
/**
 * Caído (F74, doc 09 §4, item 10; aprovado pelo dono): o golpe que mataria derruba antes. Por 10 s o
 * jogador fica no chão, sem agir e sem apanhar (as criaturas o deixam); um aliado que segura E a até
 * 2,5 m por 2 s o levanta com 30 % da Vida e 2 s invulnerável. Sem ninguém, sangra e morre (a Lápide).
 * Não vale na Arena, debaixo d'água, no dano contínuo nem sem um aliado de pé.
 */
export const FALL = { seconds: 10, liftHold: 2, liftReach: 2.5, hpFraction: 0.3, invulnerable: 2 } as const;

export const DEATH_STRUCTURE_NAMES: Record<string, string> = { cruz: "Lápide", trouxa: "Pertences" };
